import { api, apiDateFormat } from 'api'
import { path } from 'ramda'
import { format } from 'date-fns'

export type InvoiceType = 'outstanding' | 'paid'
export type InvoiceStatus = 'pending' | 'paid' | 'created'

export interface InvoicesRequest {
  companyId: number
  type: InvoiceType
  startDate?: Date
  endDate?: Date
}

export interface ChargeItem {
  id: number
  description: string
  quantity: number
  unitAmount: number
  amount: number
}

export interface Invoice {
  id: number
  totalAmount: number
  status: InvoiceStatus
  chargeDetailsUrl: string
  finalizedAt: string
  chargeItems: ChargeItem[]
  createdAt: string
  dueAt: string
  automated: boolean
  // Only populated on the single-invoice detail query (used for the PDF/receipt view);
  // the invoice list query does not request this field.
  chargeDescription?: string | null
}

export async function getInvoices(options: InvoicesRequest) {
  const { companyId, type } = options
  const startDate = options.startDate
    ? format(options.startDate, apiDateFormat)
    : ''
  const endDate = options.endDate ? format(options.endDate, apiDateFormat) : ''
  const response = await api.post(
    `/company/graphql_dispatch/${companyId}/receipts_fdd598d1`,
    {
      type,
      startDate,
      endDate,
    }
  )

  return path(['data', 'data', 'company', 'invoices'], response) as Invoice[]
}

interface InvoiceCSVDataOptions {
  type: 'summary' | 'details'
  startDate: Date
  endDate: Date
}

export async function getInvoiceCSVData(
  companyId: number,
  options: InvoiceCSVDataOptions
) {
  const { type, startDate, endDate } = options

  const url =
    type === 'details'
      ? `/company/${companyId}/money/paid_charge_details`
      : `/company/${companyId}/money/paid_charge_summary`

  const data = await api.get(url, {
    params: {
      start_date: format(startDate, apiDateFormat),
      end_date: format(endDate, apiDateFormat),
    },
  })

  return path(['data'], data)
}

export interface InvoiceDetail {
  companyName: string
  chargeUpfront?: boolean
  primaryContact: {
    name: string
    email: string
  }
  invoice: Invoice
}

export async function getInvoice(
  invoiceId: number,
  companyId: number
): Promise<InvoiceDetail | null> {
  const data = await api.post(
    `/company/graphql_dispatch/${companyId}/receipt_98cb4ea2`,
    {
      invoiceId,
    }
  )

  const detail = path(['data', 'data', 'company'], data)
  if (!detail) {
    return null
  }

  const invoice = path(['invoice'], detail)
  if (!invoice) {
    return null
  }

  return {
    companyName: detail.name,
    chargeUpfront: detail.chargeUpfront,
    primaryContact: path(['admins', 0], detail),
    invoice,
  } as InvoiceDetail
}

export async function getInvoiceDetailCSVData(url: string) {
  // Normalize the URL to match the pattern used in getInvoiceCSVData
  // If it's a full URL, extract just the path part
  let requestUrl = url
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // It's a full URL - extract the path
    try {
      const urlObj = new URL(url)
      requestUrl = urlObj.pathname + urlObj.search
    } catch {
      // If URL parsing fails, try to extract path manually
      const match = url.match(/\/v1\/.*$/)
      if (match) {
        requestUrl = match[0]
      }
    }
  }

  // Remove /v1/ prefix if present since baseURL already includes /v1
  if (requestUrl.startsWith('/v1/')) {
    requestUrl = requestUrl.replace(/^\/v1/, '')
  }

  // Use api.get() exactly like getInvoiceCSVData does
  const data = await api.get(requestUrl)

  return path(['data'], data)
}
